import { useState } from 'react';
import { App, Button, Empty, Image, Modal, Space, Spin, Typography, Upload } from 'antd';
import { PictureOutlined, ThunderboltOutlined, UploadOutlined } from '@ant-design/icons';
import { pruneTemplateBackground, uploadTemplateBackground } from '@/api/flyers';
import { getGeneration } from '@/api/media';
import { useSetTemplateBackground } from '@/hooks/useFlyers';
import { useGenerations } from '@/hooks/useMedia';
import { GeneratePanel } from '@/pages/Studio/GeneratePanel';

interface Props {
  templateId: string | null;
  backgroundUrl: string | null;
  onChanged: () => void;
  sourceTopicNo?: string | null;
}

export function BackgroundPanel({ templateId, backgroundUrl, onChanged, sourceTopicNo }: Props) {
  const { message } = App.useApp();
  const [pickOpen, setPickOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [lastGenerated, setLastGenerated] = useState<string | null>(null);
  const setBg = useSetTemplateBackground();
  const disabled = !templateId;

  const applyGeneration = async (generationId: string) => {
    if (!templateId) return;
    try {
      await setBg.mutateAsync({ id: templateId, generationId });
      message.success('Background updated');
      setPickOpen(false); setGenOpen(false);
      onChanged();
    } catch (e) { message.error((e as Error).message); }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      {backgroundUrl
        ? <Image src={backgroundUrl} style={{ maxHeight: 120, objectFit: 'contain' }} />
        : <Typography.Text type="secondary" style={{ fontSize: 12 }}>No background yet</Typography.Text>}
      {disabled && <Typography.Text type="warning" style={{ fontSize: 12 }}>Save the template first to set a background.</Typography.Text>}
      <Space wrap>
        <Upload
          showUploadList={false} accept="image/*" disabled={disabled}
          customRequest={async ({ file, onSuccess, onError }) => {
            try {
              const res = await uploadTemplateBackground(templateId!, file as File);
              onSuccess?.({});
              message.success('Uploaded');
              try { await pruneTemplateBackground(templateId!, res.uuid); }
              catch (e) { message.warning(`Upload succeeded, but couldn't remove the old background: ${(e as Error).message}`); }
              onChanged();
            } catch (e) { onError?.(e as Error); message.error((e as Error).message); }
          }}>
          <Button size="small" icon={<UploadOutlined />} disabled={disabled}>Upload</Button>
        </Upload>
        <Button size="small" icon={<PictureOutlined />} disabled={disabled} onClick={() => setPickOpen(true)}>From library</Button>
        <Button size="small" icon={<ThunderboltOutlined />} disabled={disabled} onClick={() => setGenOpen(true)}>Generate</Button>
      </Space>

      <Modal open={pickOpen} onCancel={() => setPickOpen(false)} footer={null} width={720} title="Pick a background from the media library">
        <LibraryPicker onPick={applyGeneration} busy={setBg.isPending} />
      </Modal>

      <Modal open={genOpen} onCancel={() => setGenOpen(false)} footer={null} width={520} title="Generate a background">
        <Space direction="vertical" style={{ width: '100%' }}>
          <GeneratePanel allowedTasks={['t2i', 'i2i']} initialTopicNo={sourceTopicNo ?? undefined}
            onGenerated={async (id) => {
              try {
                const g = await getGeneration(id);
                if (g.status === 'succeeded') setLastGenerated(id);
              } catch { /* still generating or fetch failed — keep button disabled */ }
            }} />
          <Button type="primary" block disabled={!lastGenerated} loading={setBg.isPending}
            onClick={() => lastGenerated && applyGeneration(lastGenerated)}>
            Use latest result as background
          </Button>
        </Space>
      </Modal>
    </Space>
  );
}

function LibraryPicker({ onPick, busy }: { onPick: (generationId: string) => void; busy: boolean }) {
  const q = useGenerations({ modality: 'image', status: 'succeeded', pageSize: 36 });
  const items = q.data?.data ?? [];
  if (q.isLoading) return <Spin />;
  if (items.length === 0) return <Empty description="No generated images yet" />;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
      {items.map((g) => (
        <div key={g.id} style={{ cursor: busy ? 'wait' : 'pointer' }} onClick={() => !busy && onPick(g.id)}>
          {g.results[0] && <Image preview={false} src={g.results[0].url} height={110} style={{ objectFit: 'cover', borderRadius: 4, width: '100%' }} />}
          <Typography.Paragraph ellipsis={{ rows: 1 }} style={{ fontSize: 11, margin: '4px 0 0' }}>{g.prompt}</Typography.Paragraph>
        </div>
      ))}
    </div>
  );
}
